"use client";

import React, { useContext } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import classnames from "classnames";
import Button from "../../atoms/Button";
import RichText from "@/components/organisms/Richtext";
import Wrapper from "@/components/organisms/Wrapper";
import PhoneIcon from "../../../assets/svg/phone.svg";
import SiliconValleyLogo from "@/utils/SiliconValleyLogo";
import { FooterDisclaimerProps } from "./types";
import Image from "next/image";
import { GlobalContext } from "@/contexts/SiteDataContext";
import { useViewModel } from "./useViewModel";
import "./styles.scss";

const FooterDisclaimer: React.FC<FooterDisclaimerProps> = ({ theme, hideContactInfo }) => {
  const {
    showContactInfo,
    showSiliconValleyLogo,
    showSiteAddress,
    isFormGeniusCompany,
    isMobile,
    disclaimer,
    contactPhoneNumber,
    siteAddress,
  } = useViewModel({ hideContactInfo });
  const { t } = useTranslation("footer");
  const { t: disclaimerTranslation } = useTranslation("footerDisclaimer");
  const {
    siteData: { metadata },
    siteDataComplete,
  } = useContext(GlobalContext).context;
  const logo_filename = metadata?.logo_filename || "logo.svg";

  return (
    <div className="footer--body">
      <Wrapper
        rowReverse
        className="u-flex">
        <div>
          {isMobile && showContactInfo && (
            <div className="footer__contact-mobile u-mobile-only u-align-center">
              <Button
                className="footer__contact-button"
                primaryAlt
                data-qa="footer-call"
                label={contactPhoneNumber}
                link={`tel:${contactPhoneNumber}`}
                buttonType="link"
                svg={<PhoneIcon />}
                iconLeft
                full
              />
              <span className="footer__contact-hours">{`${t("Contact hours:")} ${t("Mon-Fri 5am - 7pm PT")}`}</span>
              {showSiliconValleyLogo && <SiliconValleyLogo />}
            </div>
          )}
          <div
            className={classnames("footer__contact", {
              "footer__contact--only-logo": isFormGeniusCompany && !showContactInfo,
              "u-desktop-only": isFormGeniusCompany && showContactInfo,
            })}>
            {showContactInfo && (
              <>
                <Image
                  width={150}
                  height={50}
                  className="footer__contact-logo"
                  id="company_logo"
                  src={`/assets/logos/${theme}/site/${logo_filename}`}
                  alt="logo"
                />
                <div className="footer__contact-title">{t("Contact Us")}</div>
                <div>
                  <span className="footer__contact-phone">{contactPhoneNumber}</span>
                  <span className="footer__contact-hours">{t("Mon-Fri 5am - 7pm PT")}</span>
                  {showSiteAddress && (
                    <span className="footer__contact-address">{siteAddress}</span>
                  )}
                </div>
              </>
            )}
            {showSiliconValleyLogo && <SiliconValleyLogo />}
          </div>
        </div>
        <div>
          {disclaimer && (
            <RichText
              content={disclaimer}
              t={disclaimerTranslation}
              siteDataComplete={siteDataComplete}
            />
          )}
        </div>
      </Wrapper>
    </div>
  );
};

export default FooterDisclaimer;
